<template>
	<v-dialog v-model="dialogScheduleTour" width="500">
		<template v-slot:activator="{ on }">
			<v-btn dark color="blue" v-on="on" @click="ping">
				<v-icon left>event</v-icon>
				Schedule Tour
			</v-btn>
		</template>

		<!-- The success message card. -->
		<app-schedule-tour-success
			v-if="success"
			@close="dialogScheduleTour = false"
		/>

		<!-- The error message card. -->
		<app-schedule-tour-error
			v-else-if="isErrMsg"
			:err-msg="errMsg"
			@close="dialogScheduleTour = false"
		/>

		<!-- The card with the form to schedule a tour, displayed by default. -->
		<v-card v-else>
			<app-schedule-tour-title @close="dialogScheduleTour = false" />

			<v-card-text>
				<v-form ref="form">
					<!-- Name, email, phone number text fields. -->
					<v-text-field v-model="form.fullName" prepend-icon="person" label="Full Name" required />
					<v-text-field v-model="form.email" prepend-icon="mail_outline" label="E-mail Address" required />
					<v-text-field v-model="form.phone" prepend-icon="call" label="Phone Number" required />

					<!-- Tour date input field. -->
					<v-menu transition="scale-transition" max-width="290px" min-width="290px">
						<template v-slot:activator="{ on }">
							<v-text-field
								v-model="form.tourDate"
								label="Tour Date"
								hint="YYYYMMDD"
								persistent-hint
								prepend-icon="event"
								v-on="on"
							/>
						</template>
						<v-date-picker no-title :allowed-dates="allowedDates" @input="onDatePickerClosed" />
					</v-menu>

					<!-- Time slot select input. -->
					<v-select
						v-model="form.timeSlot"
						prepend-icon="access_time"
						label="Time Slot"
						:items="timeSlots"
						:disabled="form.tourDate === null"
						hint="Please contact us if there is no convenient time slot for you."
						persistent-hint
					/>

					<!-- Age ranges -->
					<v-select
						v-model="form.ageRanges"
						prepend-icon="list_alt"
						label="Age Ranges"
						:items="programs"
						multiple
					/>

					<!-- Additional information textarea. -->
					<v-textarea
						v-model="form.additionalInfo"
						prepend-icon="subject"
						label="Any Additional Information?"
						persistent-hint
						rows="2"
					/>

					<!-- Spam prevention. -->
					<div class="input-section mt-4">
						<v-icon>block</v-icon>
						<vue-recaptcha
							ref="recaptcha"
							class="ml-2"
							sitekey="6Lfqb4kUAAAAABZ8N1aIBGv4lHS3YlS7_XqQ-Fru"
							@verify="onCaptchaVerified"
							@expired="onCaptchaExpired"
						/>
					</div>
				</v-form>
			</v-card-text>

			<v-card-actions class="px-5 pb-3 justify-space-between">
				<v-btn color="success" @click="onFormSubmit">Submit</v-btn>
				<v-btn color="error" @click="onFormReset">Reset Form</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import AppScheduleTourError   from '@/components/schedule-tour/Error.vue'
import AppScheduleTourSuccess from '@/components/schedule-tour/Success.vue'
import AppScheduleTourTitle   from '@/components/schedule-tour/Title.vue'

import VueRecaptcha from 'vue-recaptcha'

export default {
	components: {
		AppScheduleTourError,
		AppScheduleTourSuccess,
		AppScheduleTourTitle,
		VueRecaptcha,
	},

	data: () => ({
		programs: [
			'6 weeks - 18 months',
			'18 months - 2 years',
			'2 - 3 years',
			'3 - 5 1/2 years',
			'Before and After Care',
		],
		success: false,
		menuTourDate: false,
		dialogScheduleTour: false,
		errMsg: '',
		timeSlots: [],
		form: {
			fullName: '',
			phone: '',
			email: '',
			tourDate: '',
			timeSlot: null,
			ageRanges: [],
			additionalInfo: '',
			captchaToken: null,
		},
	}),

	computed: {
		isErrMsg() {
			return this.errMsg && this.errMsg !== ''
		},
	},

	methods: {
		// Make sure the server is reachable.
		ping() {
			this.$http.get(this.$api + '/unavailable_timeslots?tourDate=00000001')
				.then((res) => {
					this.errMsg = null // Cancel any existing error message.
				})
				.catch((e) => {
					this.errMsg = 'Sorry, our servers are unreachable.' // Pass the error message on to the user.
				})
		},

		// Given the date in 'YYYY-MM-DD' format, return
		// true if enabled, false if disabled.
		allowedDates: function(val) {
			const valDate = new Date(val)

			// Return 'false' if the date is a weekend.
			const valDay = valDate.getDay()
			if (valDay === 0 || valDay === 5 || valDay === 6) {
				return false
			}

			// Disable if the date is before the current
			// date ("today") or more than 30 days in advance.
			const today = new Date()
			const todayPlus30 = new Date().setDate(today.getDate() + 30)
			if (valDate < today || valDate > todayPlus30) {
				return false
			}

			// Enable the rest (weekdays less than 30 days in advance).
			return true
		},

		// When the date picker is closed, whether a date is selected or the
		// user just clicks outside the date picker and no date is selected.
		onDatePickerClosed(tdVal) {
			if (!tdVal) { // No date is selected; nothing to do.
				return
			}

			// A date was selected. Let's put the selected date in YYYYMMDD format.
			this.form.tourDate = parseInt(tdVal.replace(/\-/g, ''), 10)

			// Now that we have a YYYYMMDD formatted date, let's use it
			// to get which time slots are already taken for that day.
			// Prepare a GET request.
			this.$http.get(this.$api + '/unavailable_timeslots?tourDate=' + this.form.tourDate)
				.then(resp => { // The GET request was successful. Let's process the response.
					console.log(JSON.stringify(resp.data))

					// There were no errors.
					// This is the default state of the time slot drop down.
					this.timeSlots = [
						{ text: 'Select a time slot', value: null },
						{ text: '9 a.m. - 10 a.m.',   value: 0 },
						{ text: '10 a.m. - 11 a.m.',  value: 1 },
						{ text: '11 a.m. - 12 a.m.',  value: 2 },
						{ text: '2 p.m. - 3 p.m.',    value: 3 },
						{ text: '3 p.m. - 4 p.m.',    value: 4 },
						{ text: '4 p.m. - 5 p.m.',    value: 5 },
						{ text: '5 p.m. - 6 p.m.',    value: 6 },
					]

					// Let's see if there were any taken time slots sent back,
					// and if there are, remove them from selection.
					if (resp.data && resp.data.length) {
						// Let's remove the taken time slots.
						for (let i = resp.data.length - 1; i >= 0; i--) {
							this.timeSlots.splice(resp.data[i] + 1, 1)
						}
					}
				})
				.catch((err) => {
					// The GET request was not successful. Let's pass the error message on to the user.
					this.errMsg = 'Sorry, there was an error with your request.' // err.message
					console.log(err.message)
				})
		},

		// The user successfully passed the "I'm a human" test.
		// Set the captchaToken form variable to the verification token.
		onCaptchaVerified(token) {
			this.form.captchaToken = token
		},

		// When the recaptcha verification has expired, reset the
		// recaptcha component and the form value.
		onCaptchaExpired() {
			this.$refs.recaptcha.reset()
			this.form.captchaToken = null
		},

		onFormReset() {
			this.$refs.form.reset()
			this.$refs.recaptcha.reset()
			this.form.captchaToken = null
			this.errMsg = ''
		},

		// When the form is submitted, send a POST request to the server with the form data.
		onFormSubmit(evt) {
			evt.preventDefault()

			this.$http.post(this.$api + '/schedule_tour', this.form)
				.then(resp => { // The POST request was successful.
					// First let's check if there were any errors sent back from the server.
					if (resp.data.error) {
						this.errMsg = 'Our server was unable to process the information you provided.'
						console.log(resp.data.error)
						return
					}

					// There were no errors, let's show the success modal.
					this.success = true
				})
				.catch((err) => {
					this.errMsg = 'Our server was unable to process the information you provided.'
					console.log(err.message)
				})
		},
	},
}
</script>

<style>
.input-section * {
	display: inline;
	vertical-align: top;
}
</style>
